/**
 * 監査ログの一覧(実データ)。ページングと絞り込み・期間はサーバー側で行う。
 *
 * 見える範囲は API が決める(`suite:admin` は全件、`<app>:admin` は自分のアプリの行だけ)。
 * CSV も同じ絞り込み・同じ見える範囲(`GET /api/core/audit/export.csv`)。
 */
import { DownloadOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Card, DatePicker, Flex, Input, Space, Table, Tag, Typography, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";

import { useApi } from "../lib/useApi";
import type { AuditRow } from "../lib/api";
import { jst } from "../lib/format";

/** 期間のプリセット(idp のログ画面と同じ並び)。 */
const PRESETS = [
  { days: 1, label: "今日" },
  { days: 7, label: "7 日" },
  { days: 30, label: "30 日" },
];

function lastDays(days: number): [Dayjs, Dayjs] {
  const end = dayjs();
  return [days <= 1 ? end.startOf("day") : end.subtract(days, "day"), end];
}

const columns: ColumnsType<AuditRow> = [
  {
    title: "日時 (JST)",
    dataIndex: "created_at",
    width: 180,
    render: (v: string) => jst(v),
  },
  { title: "実行者", dataIndex: "actor", width: 160 },
  { title: "操作", dataIndex: "action", width: 180, render: (v: string) => <Tag>{v}</Tag> },
  { title: "アプリ", dataIndex: "app", width: 160 },
  { title: "対象", dataIndex: "target", width: 240, },
  { title: "詳細", width: 320, dataIndex: "detail", ellipsis: true },
];

export function AuditPage() {
  const api = useApi();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  // 入力中の値と、実際に問い合わせに使う値を分ける(1文字ごとに叩かない)
  const [actorInput, setActorInput] = useState("");
  const [actionInput, setActionInput] = useState("");
  const [filters, setFilters] = useState({ actor: "", action: "" });
  // 期間は入れた瞬間に効かせる(絞り込みの欄と違って打ち間違いが無い)
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);

  const params = (withPaging: boolean) =>
    new URLSearchParams({
      ...(withPaging
        ? { limit: String(pageSize), offset: String((page - 1) * pageSize) }
        : {}),
      ...(filters.actor ? { actor: filters.actor } : {}),
      ...(filters.action ? { action: filters.action } : {}),
      ...(range ? { from: range[0].toISOString(), to: range[1].toISOString() } : {}),
    });

  const query = useQuery({
    queryKey: ["audit", page, pageSize, filters, range?.[0]?.valueOf(), range?.[1]?.valueOf()],
    queryFn: () => api.audit(params(true)),
  });

  const search = () => {
    setPage(1);
    setFilters({ actor: actorInput, action: actionInput });
  };

  const clear = () => {
    setActorInput("");
    setActionInput("");
    setPage(1);
    setRange(null);
    setFilters({ actor: "", action: "" });
  };

  /** CSV。画面と同じ絞り込みで落とす(`limit` / `offset` は渡さない)。 */
  const csv = async () => {
    try {
      const { blob, filename } = await api.download(
        `/api/core/audit/export.csv?${params(false).toString()}`,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      void message.error(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Typography.Title level={3}>監査ログ</Typography.Title>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Flex gap={8} wrap>
          <Input
            placeholder="実行者(部分一致)"
            value={actorInput}
            onChange={(e) => setActorInput(e.target.value)}
            onPressEnter={search}
            style={{ width: 220 }}
            allowClear
          />
          <Input
            placeholder="操作(例 settings.update)"
            value={actionInput}
            onChange={(e) => setActionInput(e.target.value)}
            onPressEnter={search}
            style={{ width: 260 }}
            allowClear
          />
          <DatePicker.RangePicker
            showTime
            value={range}
            onChange={(v) => {
              setPage(1);
              setRange(v && v[0] && v[1] ? [v[0], v[1]] : null);
            }}
          />
          <Space>
            {PRESETS.map((p) => (
              <Button
                key={p.days}
                size="small"
                onClick={() => {
                  setPage(1);
                  setRange(lastDays(p.days));
                }}
              >
                {p.label}
              </Button>
            ))}
          </Space>
          <Space>
            <Button type="primary" onClick={search}>
              絞り込む
            </Button>
            <Button onClick={clear}>解除</Button>
          </Space>
          <div style={{ flex: 1 }} />
          <Button icon={<DownloadOutlined />} onClick={() => void csv()}>
            CSV
          </Button>
        </Flex>
      </Card>

      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="監査ログを取得できなかった"
          description={query.error instanceof Error ? query.error.message : String(query.error)}
        />
      )}

      <Table<AuditRow>
        scroll={{ x: "max-content" }}
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={query.data?.items ?? []}
        loading={query.isFetching}
        pagination={{
          current: page,
          pageSize,
          total: query.data?.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: [20, 50, 100],
          showTotal: (total) => `全 ${total} 件`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />
    </div>
  );
}
